import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, MessageCircle, Phone } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR, enUS, es as esLocale, fr as frLocale } from 'date-fns/locale';
import { Header } from './Header';
import { Footer } from './Footer';
import { WhatsAppButton } from './WhatsAppButton';
import { Button } from './ui/button';
import { getPostBySlug } from '../data/blog';
import { SUPPORTED_LANGUAGES } from '../i18n';

const dateLocaleMap = { pt: ptBR, en: enUS, es: esLocale, fr: frLocale };
const SITE_URL = 'https://govipportugal.com';

const upsertMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(attrs.tagName || 'meta');
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (k === 'tagName') return;
    el.setAttribute(k, v);
  });
};

const removeAll = (selector) => {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
};

export const BlogPost = () => {
  const { t, i18n } = useTranslation();
  const { lang, slug } = useParams();
  const currentLang = lang || i18n.language || 'en';
  const dateLocale = dateLocaleMap[currentLang] || enUS;
  const post = getPostBySlug(slug, currentLang);

  // ---- SEO injection ----
  useEffect(() => {
    if (!post) return;
    document.title = post.title;
    upsertMeta('meta[name="description"]', { name: 'description', content: post.description });
    upsertMeta('meta[name="keywords"]', { name: 'keywords', content: post.keywords });

    // Open Graph
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: post.title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: post.description });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'article' });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: post.image });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: `${SITE_URL}/${currentLang}/blog/${post.slug}`,
    });
    upsertMeta('meta[property="article:published_time"]', {
      property: 'article:published_time',
      content: post.publishDate,
    });

    // Twitter
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: post.image });

    // Canonical
    upsertMeta('link[rel="canonical"]', {
      tagName: 'link',
      rel: 'canonical',
      href: `${SITE_URL}/${currentLang}/blog/${post.slug}`,
    });

    // hreflang for all available translations of this post
    removeAll('link[rel="alternate"][data-hreflang]');
    SUPPORTED_LANGUAGES.forEach((code) => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', code);
      link.setAttribute('href', `${SITE_URL}/${code}/blog/${post.slug}`);
      link.setAttribute('data-hreflang', '1');
      document.head.appendChild(link);
    });
    const xDef = document.createElement('link');
    xDef.setAttribute('rel', 'alternate');
    xDef.setAttribute('hreflang', 'x-default');
    xDef.setAttribute('href', `${SITE_URL}/en/blog/${post.slug}`);
    xDef.setAttribute('data-hreflang', '1');
    document.head.appendChild(xDef);

    // Schema.org Article + BreadcrumbList
    const schemaId = 'govip-article-jsonld';
    let schemaTag = document.getElementById(schemaId);
    if (!schemaTag) {
      schemaTag = document.createElement('script');
      schemaTag.id = schemaId;
      schemaTag.type = 'application/ld+json';
      document.head.appendChild(schemaTag);
    }
    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: post.title,
          description: post.description,
          image: post.image,
          datePublished: post.publishDate,
          dateModified: post.publishDate,
          inLanguage: currentLang,
          author: {
            '@type': 'Organization',
            name: 'GO VIP Portugal',
            url: SITE_URL,
          },
          publisher: {
            '@type': 'Organization',
            name: 'GO VIP Portugal',
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo-govip.jpg` },
          },
          mainEntityOfPage: `${SITE_URL}/${currentLang}/blog/${post.slug}`,
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: `${SITE_URL}/${currentLang}`,
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Blog',
              item: `${SITE_URL}/${currentLang}/blog`,
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: post.heading || post.title,
            },
          ],
        },
      ],
    };
    schemaTag.textContent = JSON.stringify(schema);
  }, [post, currentLang, slug]);

  if (!post) return <Navigate to={`/${currentLang}/blog`} replace />;

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero with image */}
      <section className="relative pt-32 pb-12 bg-gradient-to-b from-gray-900 to-gray-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900/95 via-gray-900/70 to-gray-900/30"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link
              to={`/${currentLang}/blog`}
              className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 mb-6 text-sm font-medium transition-colors"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="w-4 h-4" />
              {t('blog.back_to_list')}
            </Link>
            <span className="inline-block bg-amber-600 text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded-full mb-4">
              {t(`blog.categories.${post.category}`)}
            </span>
            <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-5" data-testid="post-title">
              {post.heading || post.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-amber-100/90">
              <span className="flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {format(new Date(post.publishDate), 'PPP', { locale: dateLocale })}
              </span>
              <span>•</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.readMinutes} {t('blog.min_read')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <p className="text-xl text-gray-700 leading-relaxed mb-10 font-light italic border-l-4 border-amber-500 pl-5">
              {post.excerpt}
            </p>
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
              data-testid="post-content"
            />

            {/* CTA Card */}
            <div className="mt-14 rounded-2xl bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 p-8 shadow-md">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('blog.cta_title')}</h3>
              <p className="text-gray-700 mb-6">{t('blog.cta_description')}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to={`/${currentLang}#booking`}>
                  <Button className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-5 w-full sm:w-auto">
                    {t('blog.cta_book')}
                  </Button>
                </Link>
                <a href="https://wa.me/351932651940" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white px-6 py-5 w-full sm:w-auto">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </Button>
                </a>
                <a href="tel:+351932651940">
                  <Button variant="outline" className="border-gray-300 px-6 py-5 w-full sm:w-auto">
                    <Phone className="w-4 h-4 mr-2" />
                    +351 932 651 940
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};
